/**
 * Two separate "environment" concepts exist in IMS, and this plugin keeps
 * them apart:
 *
 * - Deployment: which IMS API instance we talk to (staging vs production
 *   ALB). Picks the proxy entry and is the first URL segment
 *   (/ims-control-panel/:deployment/...).
 * - Catalog environment: the `:environment` path param on the API and the
 *   `environment` field on every entity — which environment's
 *   infrastructure an entity describes. One deployment can manage several
 *   (e.g. IMS staging holding `dev` and `staging` catalogs). Discovered per
 *   deployment via GET /v1/environments, selected via a header dropdown,
 *   and the second URL segment, so links pin both dimensions.
 */
export type ImsDeployment = 'staging' | 'production';

export const IMS_DEPLOYMENTS: ImsDeployment[] = ['staging', 'production'];

export const IMS_DEPLOYMENT_LABELS: Record<ImsDeployment, string> = {
  staging: 'IMS-STAGING',
  production: 'IMS-PROD',
};

/** Proxy entries in app-config.yaml / app-config.dev.yaml, one per deployment. */
export const IMS_PROXY_PATHS: Record<ImsDeployment, string> = {
  staging: '/ims-management-api-staging',
  production: '/ims-management-api-production',
};

export function isImsDeployment(value: string): value is ImsDeployment {
  return (IMS_DEPLOYMENTS as string[]).includes(value);
}

/**
 * By convention each deployment holds a catalog named after itself; used as
 * the landing environment when a URL does not pin one.
 */
export function defaultCatalogEnvironment(deployment: ImsDeployment): string {
  return deployment;
}

export const IMS_CONTROL_PANEL_BASE_URL = '/ims-control-panel';

/** Path segment of a catalog environment's audit log, beside the kind tabs. */
export const IMS_AUDIT_EVENTS_PATH = 'audit-events';

export const DEFAULT_IMS_DEPLOYMENT: ImsDeployment = 'production';

export const IMS_PAGE_TITLE = 'IMS Control Panel';
